// CHANGE: Removed all debug console statements for production
// User management interface for Admins
// Allows creating, viewing, and managing users with role-based access
// Updated with modern dashboard design and improved styling
// CHANGE: Modified to support multiple sites per user (many-to-many relationship)
// CHANGE: Added sorting functionality for all columns
// CHANGE: Added user activation/deactivation functionality with archived users view
// CHANGE: Harmonized design with UnifiedView: reduced title size, improved spacing, modern shadows
// CHANGE: Added sidebar toggle button for collapsible sidebar feature
// CHANGE: Added search bar and filters for better user discovery
// UPDATED: Migrated to centralized toast system for all notifications
// CHANGE: Added CSV import functionality for bulk user creation
// CHANGE: Added read-only mode for director role - can view all users but cannot create/modify/delete
// CHANGE: Added mobile responsive card view with ResponsiveDataView and ResponsiveDataCard
// CHANGE: Added ReadOnlyBanner to inform users they are in consultation mode on mobile
// UPDATED: Implemented server-side pagination with infinite scroll for better performance
// UPDATED: Mobile header now has white background with bottom border separator matching bottom nav styling
// UPDATED: Added MobileSearchFilters and MobileFilterBottomSheet for mobile-optimized filtering
// UPDATED: Added skeleton loading states for improved perceived performance
// FIXED: Corrected modal component usage - removed unsupported props (isOpen, isCreation, onSave, existingUser)
// FIXED: Standardized to conditional rendering pattern for all modals (UserFormModal, UserImportModal, PasswordResetModal)
// FIXED: Added proper prop name corrections (onSubmit, initialData, onImportComplete)
// FIXED: Removed lg:hidden class from sidebar toggle button - now visible on all screen sizes
// FIXED: Harmonized sidebar toggle button style with other admin pages (SiteManagement, ServiceManagement)
// UX: Moved "Désactivés" button to header for consistency with Sites/Services pages
// UX: Enhanced search to support multiple fields (username, first_name, last_name, email)
// UX: Added progress indicator showing "X chargés sur Y" for infinite scroll
// OPTIMIZATION: Migrated to server-side filtering with RPC function for multi-site filtering
// - Replaced client-side site filtering with PostgreSQL RPC call
// - Removed separate loading of user_sites (now handled by RPC)
// - Pagination now works correctly with all filters including sites
// - Improved performance by only loading filtered results from database
// CHANGE: Reset button now only shows when dropdown filters are active (not for search text, which has its own clear X button)
// REFACTOR: Moved direct supabase.from() sites query to getActiveSites() service function

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, ArrowUpDown, ArrowUp, ArrowDown, UserX, UserCheck, ArrowLeft, KeyRound, Pencil as Edit, Upload, MapPin } from 'lucide-react';
import { supabase, getFullName } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { usePaginatedData } from '../../hooks/usePaginatedData';
import type { Site, UserWithSites } from '../../lib/supabase';
import { createUser, updateUser, toggleUserStatus, resetUserPassword } from '../../lib/auth';
import { getActiveSites } from '../../lib/sites';
import { UserFormModal } from './UserFormModal';
import { UserImportModal } from './UserImportModal';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { Tooltip } from '../ui/Tooltip';
import { PasswordResetModal } from './PasswordResetModal';
import { DropdownMenu } from '../ui/DropdownMenu';
import type { DropdownMenuItem } from '../ui/DropdownMenu';
import { ConsolidatedControlBar } from '../ui/ConsolidatedControlBar';
import type { FilterOption } from '../ui/FilterDropdown';
import { MobileSearchFilters } from '../ui/MobileSearchFilters';
import { MobileFilterBottomSheet, type FilterGroup } from '../ui/MobileFilterBottomSheet';
import { ResponsiveDataView } from '../ui/ResponsiveDataView';
import { ResponsiveDataCard } from '../ui/ResponsiveDataCard';
import { ReadOnlyBanner } from '../ui/ReadOnlyBanner';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { BouncingDotsLoader } from '../ui/BouncingDotsLoader';
import RoleBadge from '../ui/RoleBadge';
import type { UserRole } from '../../lib/roleThemes';

type SortField = 'username' | 'last_name' | 'role';
type SortOrder = 'asc' | 'desc';

export function UserManagement() {
  const { user: currentUser } = useAuth();
  const { isMobile, isDesktop } = useResponsiveLayout();
  const [sites, setSites] = useState<Site[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const { showToast } = useToast();

  // Check if current user is director or on mobile (read-only mode)
  const isReadOnly = currentUser?.role === 'director' || isMobile;
  const [sortField, setSortField] = useState<SortField>('last_name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [deactivatingUser, setDeactivatingUser] = useState<UserWithSites | null>(null);
  const [activatingUser, setActivatingUser] = useState<UserWithSites | null>(null);
  const [resettingPassword, setResettingPassword] = useState<UserWithSites | null>(null);
  const [resetPasswordResult, setResetPasswordResult] = useState<{ username: string; password: string } | null>(null);
  const [editingUser, setEditingUser] = useState<UserWithSites | null>(null);
  const [creationPasswordResult, setCreationPasswordResult] = useState<{ username: string; password: string } | null>(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Build RPC params for server-side filtering
  const rpcParams = useMemo(() => {
    const params: Record<string, any> = {
      p_is_active: !showDeactivated,
    };

    // Add role filter
    if (selectedRoles.length > 0) {
      params.p_role_ids = selectedRoles;
    }

    // Add site filter (server-side!)
    if (selectedSites.length > 0) {
      params.p_site_ids = selectedSites;
    }

    return params;
  }, [showDeactivated, selectedRoles, selectedSites]);

  // Use paginated data hook with RPC for server-side filtering
  const {
    items: users,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    refresh,
    totalCount,
  } = usePaginatedData<UserWithSites>({
    supabase,
    rpcFunction: 'get_users_with_site_filter',
    rpcParams,
    pageSize: 20,
    orderBy: { column: sortField, ascending: sortOrder === 'asc' },
    searchTerm,
  });

  // Load sites only (no need for user_sites separately)
  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      const result = await getActiveSites();
      if (result.success && result.data) {
        setSites(result.data);
      }
    } catch (err) {
    }
  };

  const handleSaveUser = async (userData: {
    username: string;
    firstName: string;
    lastName: string;
    role: string;
    siteIds: string[];
    email?: string;
  }, userId?: string) => {
    try {
      let result;

      if (userId) {
        result = await updateUser(
          userId,
          userData.username,
          userData.firstName,
          userData.lastName,
          userData.role as UserRole,
          userData.siteIds
        );
        if (result.success) {
          showToast('Utilisateur modifié avec succès', 'success');
          setEditingUser(null);
        } else {
          showToast(result.error || 'Erreur lors de la modification de l\'utilisateur', 'error');
        }
      } else {
        result = await createUser(
          userData.username,
          userData.firstName,
          userData.lastName,
          '', // password ignoré, sera auto-généré
          userData.role as UserRole,
          userData.siteIds
        );
        if (result.success && result.tempPassword) {
          setCreationPasswordResult({
            username: userData.username,
            password: result.tempPassword
          });
          showToast('Utilisateur créé avec succès', 'success');
        } else {
          showToast(result.error || 'Erreur lors de la création de l\'utilisateur', 'error');
        }
      }

      if (result.success) {
        setShowModal(false);
        refresh();
        loadSites();
      }

      return result;
    } catch (err) {
      const errorMessage = userId
        ? 'Erreur lors de la modification de l\'utilisateur'
        : 'Erreur lors de la création de l\'utilisateur';
      showToast(errorMessage, 'error');
      return { success: false, error: errorMessage };
    }
  };

  const handleDeactivateUser = async (user: UserWithSites) => {
    setDeactivatingUser(user);
  };

  const performDeactivate = async () => {
    if (!deactivatingUser) return;

    try {
      const result = await toggleUserStatus(deactivatingUser.id, false);

      if (!result.success) {
        showToast(result.error || 'Erreur lors de la désactivation de l\'utilisateur', 'error');
        setDeactivatingUser(null);
        return;
      }

      setDeactivatingUser(null);
      showToast('Utilisateur désactivé avec succès', 'success');
      refresh();
    } catch (err) {
      showToast('Erreur lors de la désactivation de l\'utilisateur', 'error');
      setDeactivatingUser(null);
    }
  };

  const handleActivateUser = async (user: UserWithSites) => {
    setActivatingUser(user);
  };

  const performActivate = async () => {
    if (!activatingUser) return;

    try {
      const result = await toggleUserStatus(activatingUser.id, true);

      if (!result.success) {
        showToast(result.error || 'Erreur lors de la réactivation de l\'utilisateur', 'error');
        setActivatingUser(null);
        return;
      }

      setActivatingUser(null);
      showToast('Utilisateur réactivé avec succès', 'success');
      refresh();
    } catch (err) {
      showToast('Erreur lors de la réactivation de l\'utilisateur', 'error');
      setActivatingUser(null);
    }
  };

  const handleResetPassword = async (user: UserWithSites) => {
    setResettingPassword(user);
  };

  const performResetPassword = async () => {
    if (!resettingPassword) return;

    try {
      const result = await resetUserPassword(resettingPassword.id);

      if (result.success && result.tempPassword) {
        setResetPasswordResult({
          username: resettingPassword.username,
          password: result.tempPassword
        });
        setResettingPassword(null);
        refresh();
      } else {
        showToast(result.error || 'Erreur lors de la réinitialisation du mot de passe', 'error');
        setResettingPassword(null);
      }
    } catch (err) {
      showToast('Erreur lors de la réinitialisation du mot de passe', 'error');
      setResettingPassword(null);
    }
  };

  const getRoleName = (role: string) => {
    const roles = {
      operator: 'Opérateur',
      manager: 'Manager',
      director: 'Directeur',
      admin: 'Administrateur'
    };
    return roles[role as keyof typeof roles] || role;
  };

  const getRoleBadgeColor = (role: string): string => {
    const colors = {
      operator: 'bg-blue-100 text-blue-800 border-blue-200',
      manager: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      director: 'bg-amber-100 text-amber-800 border-amber-200',
      admin: 'bg-rose-100 text-rose-800 border-rose-200'
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const renderSitesInfo = (user: UserWithSites) => {
    if (!user.sites || user.sites.length === 0) {
      return <span className="text-gray-400 text-sm">Aucun site</span>;
    }

    if (user.sites.length <= 2) {
      return (
        <span className="text-sm text-gray-700">
          {user.sites.map(s => s.name).join(', ')}
        </span>
      );
    }

    return (
      <Tooltip
        content={user.sites.map(s => s.name).join(', ')}
        position="bottom"
        delay={200}
      >
        <span className="text-sm text-gray-700 cursor-help hover:text-gray-900 transition-colors">
          {user.sites.length} site{user.sites.length > 1 ? 's' : ''}
        </span>
      </Tooltip>
    );
  };

  const getDropdownItems = (user: UserWithSites): DropdownMenuItem[] => {
    // Read-only mode for directors - no actions available
    if (isReadOnly) {
      return [];
    }

    if (showDeactivated) {
      return [
        {
          label: 'Réactiver',
          icon: <UserCheck size={16} />,
          onClick: () => handleActivateUser(user),
        },
      ];
    }

    return [
      {
        label: 'Modifier',
        icon: <Edit size={16} />,
        onClick: () => setEditingUser(user),
      },
      {
        label: 'Réinitialiser le mot de passe',
        icon: <KeyRound size={16} />,
        onClick: () => handleResetPassword(user),
      },
      {
        label: 'Désactiver',
        icon: <UserX size={16} />,
        onClick: () => handleDeactivateUser(user),
        variant: 'danger' as const,
      },
    ];
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown size={16} className="text-gray-400" />;
    }
    return sortOrder === 'asc'
      ? <ArrowUp size={16} className="text-primary-600" />
      : <ArrowDown size={16} className="text-primary-600" />;
  };

  // Filter options for desktop
  const roleFilterOptions: FilterOption[] = [
    { value: 'operator', label: 'Opérateur' },
    { value: 'manager', label: 'Manager' },
    { value: 'director', label: 'Directeur' },
    { value: 'admin', label: 'Administrateur' },
  ];

  const siteFilterOptions: FilterOption[] = sites.map(site => ({
    value: site.id,
    label: site.name,
  }));

  // Filter groups for mobile
  const mobileFilterGroups: FilterGroup[] = [
    {
      id: 'role',
      label: 'Rôle',
      options: roleFilterOptions,
      selectedValues: selectedRoles,
    },
    {
      id: 'site',
      label: 'Site',
      options: siteFilterOptions,
      selectedValues: selectedSites,
    },
  ];

  const handleMobileFilterChange = (groupId: string, values: string[]) => {
    if (groupId === 'role') {
      setSelectedRoles(values);
    } else if (groupId === 'site') {
      setSelectedSites(values);
    }
  };

  const handleClearFilters = () => {
    setSelectedRoles([]);
    setSelectedSites([]);
  };

  const handleClearAllMobileFilters = () => {
    setSelectedRoles([]);
    setSelectedSites([]);
  };

  const activeFilterCount = selectedRoles.length + selectedSites.length;
  const hasActiveFilters = activeFilterCount > 0;
  const hasAnyCriteria = searchTerm.trim() !== '' || hasActiveFilters;

  const renderUserCard = (user: UserWithSites) => {
    return (
      <ResponsiveDataCard
        primaryLabel={[user.last_name, user.first_name].filter(Boolean).join(' ') || user.username}
        secondaryLabel={user.username}
        badge={<RoleBadge role={user.role as UserRole} size="sm" />}
        metadata={[
          {
            icon: <MapPin size={16} className="text-gray-400" />,
            value: user.sites && user.sites.length > 0
              ? `${user.sites.length} site${user.sites.length > 1 ? 's' : ''}`
              : 'Aucun site'
          }
        ]}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Read-only banner for mobile */}
      {isMobile && <ReadOnlyBanner />}

      {/* Header */}
      {showDeactivated ? (
        <div className={isMobile ? 'bg-white border-b border-gray-200 -mx-4 px-4 pb-4' : ''}>
          <button
            onClick={() => setShowDeactivated(false)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors mb-4"
          >
            <ArrowLeft size={16} />
            <span>Retour</span>
          </button>
          <PageHeader title="Utilisateurs désactivés" />
        </div>
      ) : (
        <PageHeader title="Utilisateurs" isMobile={isMobile}>
          {!isReadOnly && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeactivated(true)}
                className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium px-4 py-2.5 rounded-lg transition-all duration-200 border border-gray-200 shadow-sm hover:shadow-md"
              >
                <UserX size={18} />
                <span>Désactivés</span>
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-gray-200"
              >
                <Upload size={18} />
                <span className="hidden sm:inline">Importer</span>
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Plus size={18} />
                <span>Créer</span>
              </button>
            </div>
          )}
        </PageHeader>
      )}

      {/* Search and Filters */}
      {!showDeactivated && (
        <>
          {isDesktop && (
            <ConsolidatedControlBar
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Rechercher un utilisateur..."
              itemCount={totalCount}
              itemLabel="utilisateur"
              filters={[
                {
                  label: 'Rôle',
                  options: roleFilterOptions,
                  selectedValues: selectedRoles,
                  onChange: setSelectedRoles,
                },
                {
                  label: 'Site',
                  options: siteFilterOptions,
                  selectedValues: selectedSites,
                  onChange: setSelectedSites,
                },
              ]}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={handleClearFilters}
              showSort={false}
            />
          )}

          {isMobile && (
            <MobileSearchFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onFilterClick={() => setShowMobileFilters(true)}
              activeFilterCount={activeFilterCount}
              showFilterButton={true}
              searchPlaceholder="Rechercher un utilisateur..."
            />
          )}
        </>
      )}

      {/* Users table/cards */}
      {users.length === 0 && !isLoading ? (
        <EmptyState
          message={showDeactivated ? 'Aucun utilisateur désactivé' : hasAnyCriteria ? 'Aucun utilisateur ne correspond à vos critères' : 'Aucun utilisateur trouvé'}
          actionLabel={hasActiveFilters && !showDeactivated ? 'Réinitialiser les filtres' : undefined}
          onAction={hasActiveFilters && !showDeactivated ? handleClearFilters : undefined}
        />
      ) : (
        <ResponsiveDataView
          items={users}
          isDesktop={isDesktop}
          cardRenderer={renderUserCard}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={loadMore}
          tableSkeletonComponent={
            <div className="flex items-center justify-center min-h-[400px]">
              <BouncingDotsLoader size="lg" />
            </div>
          }
          tableComponent={
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('username')}
                          className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                        >
                          <span>UTILISATEUR</span>
                          {getSortIcon('username')}
                        </button>
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('last_name')}
                          className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                        >
                          <span>NOM</span>
                          {getSortIcon('last_name')}
                        </button>
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('role')}
                          className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                        >
                          <span>RÔLE</span>
                          {getSortIcon('role')}
                        </button>
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        SITES
                      </th>
                      <th className="hidden md:table-cell text-right px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                        ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="font-semibold text-gray-900 text-sm">{user.username}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900">
                            {[user.last_name, user.first_name].filter(Boolean).join(' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <RoleBadge role={user.role as UserRole} size="md" />
                        </td>
                        <td className="px-6 py-4">
                          {renderSitesInfo(user)}
                        </td>
                        <td className="hidden md:table-cell px-6 py-4 text-right">
                          <div className="flex justify-end">
                            <DropdownMenu items={getDropdownItems(user)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          }
        />
      )}

      {/* Modals */}
      {(showModal || editingUser !== null) && (
        <UserFormModal
          onClose={() => {
            setShowModal(false);
            setEditingUser(null);
          }}
          onSubmit={async (data) => {
            const result = await handleSaveUser(
              {
                username: data.username,
                firstName: data.firstName,
                lastName: data.lastName,
                role: data.role,
                siteIds: data.siteIds || []
              },
              editingUser?.id
            );
            return result;
          }}
          sites={sites}
          initialData={editingUser ? {
            username: editingUser.username,
            firstName: editingUser.first_name,
            lastName: editingUser.last_name,
            role: editingUser.role,
            siteIds: editingUser.sites?.map(s => s.id) || []
          } : undefined}
          isEditing={editingUser !== null}
        />
      )}

      {showImportModal && (
        <UserImportModal
          sites={sites}
          onClose={() => setShowImportModal(false)}
          onImportComplete={() => {
            refresh();
            loadSites();
          }}
        />
      )}

      <ConfirmationDialog
        isOpen={deactivatingUser !== null}
        onCancel={() => setDeactivatingUser(null)}
        onConfirm={performDeactivate}
        title="Confirmer la désactivation"
        message={`Êtes-vous sûr de vouloir désactiver l'utilisateur ${deactivatingUser ? getFullName(deactivatingUser) : ''} ?`}
        confirmLabel="Désactiver"
        variant="danger"
      />

      <ConfirmationDialog
        isOpen={activatingUser !== null}
        onCancel={() => setActivatingUser(null)}
        onConfirm={performActivate}
        title="Confirmer la réactivation"
        message={`Êtes-vous sûr de vouloir réactiver l'utilisateur ${activatingUser ? getFullName(activatingUser) : ''} ?`}
        confirmLabel="Réactiver"
      />

      <ConfirmationDialog
        isOpen={resettingPassword !== null}
        onCancel={() => setResettingPassword(null)}
        onConfirm={performResetPassword}
        title="Confirmer la réinitialisation"
        message={`Êtes-vous sûr de vouloir réinitialiser le mot de passe de ${resettingPassword ? getFullName(resettingPassword) : ''} ?`}
        confirmLabel="Réinitialiser"
      />

      {resetPasswordResult && (
        <PasswordResetModal
          onClose={() => setResetPasswordResult(null)}
          username={resetPasswordResult.username}
          tempPassword={resetPasswordResult.password}
        />
      )}

      {creationPasswordResult && (
        <PasswordResetModal
          onClose={() => setCreationPasswordResult(null)}
          username={creationPasswordResult.username}
          tempPassword={creationPasswordResult.password}
        />
      )}

      {/* Mobile filter bottom sheet */}
      <MobileFilterBottomSheet
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        filterGroups={mobileFilterGroups}
        onChange={handleMobileFilterChange}
        onClearAll={handleClearAllMobileFilters}
      />
    </div>
  );
}
